import {
	Card,
	Button,
	Tag,
	Space,
	Typography,
	Row,
	Col,
	Descriptions,
	Table,
	Divider,
	notification,
	Spin,
	Badge,
	Tooltip,
	Popconfirm,
	Modal,
	Form,
	Select,
	message,
} from "antd";
import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
	PiArrowLeftDuotone,
	PiReceiptDuotone,
	PiCurrencyDollarDuotone,
	PiCalendarDuotone,
	PiUserDuotone,
	PiPackageDuotone,
	PiInfoDuotone,
	PiCheckCircleDuotone,
	PiShoppingCartDuotone,
	PiDownloadDuotone,
	PiCreditCardDuotone,
	PiCloudDuotone,
	PiGlobeDuotone,
	PiShareDuotone,
	PiClipboardDuotone,
} from "react-icons/pi";
import {
	requestGetInvoiceById,
	requestMarkInvoiceAsPaid,
	requestCreateTransaction,
	requestGetInvoiceTransactionList,
	requestProvisionInstancesFromInvoice,
	requestRegisterDomainsFromInvoice,
	requestShareInvoiceToPublic,
} from "../../services/admin.service";
import dayjs from "dayjs";
import { formatCurrency } from "../../utils/Formatter";

const { Title, Text } = Typography;

function InvoiceViewPage() {
	const navigate = useNavigate();
	const { id } = useParams();
	const [loading, setLoading] = useState(false);
	const [markPaidLoading, setMarkPaidLoading] = useState(false);
	const [invoice, setInvoice] = useState(null);
	const [transactions, setTransactions] = useState([]);
	const [transactionsLoading, setTransactionsLoading] = useState(false);
	const [transactionModalVisible, setTransactionModalVisible] = useState(false);
	const [transactionLoading, setTransactionLoading] = useState(false);
	const [provisionLoading, setProvisionLoading] = useState(false);
	const [registerLoading, setRegisterLoading] = useState(false);
	const [transactionForm] = Form.useForm();
	const [link, setLink] = useState(null);

	useEffect(() => {
		document.title = "Cloudiva Console | Invoice Details";
		fetchInvoiceDetails();
		fetchTransactions();
	}, [id]);

	useEffect(() => {
		if (invoice && invoice.links && invoice.links.length > 0) {
			const validLink = invoice.links.find(
				(link) => dayjs(link.expiredAt).isAfter(dayjs()) && link.revoked === false
			);
			setLink(
				validLink ? `https://app.cloudiva.codeiva.com/public/invoice/${validLink.token}` : null
			);
		}
	}, [invoice]);

	const fetchInvoiceDetails = () => {
		setLoading(true);
		requestGetInvoiceById(id)
			.then((response) => {
				const invoiceData = response.data.data || response.data;
				setInvoice(invoiceData);
			})
			.catch((error) => {
				console.error("Error fetching invoice details:", error);
				notification.error({
					message: "Error",
					description: "Failed to fetch invoice details",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const fetchTransactions = () => {
		setTransactionsLoading(true);
		requestGetInvoiceTransactionList(id)
			.then((response) => {
				const transactionData = response.data.data || response.data;
				setTransactions(transactionData);
			})
			.catch((error) => {
				console.error("Error fetching transactions:", error);
				// Don't show error notification for transactions as it's not critical
			})
			.finally(() => {
				setTransactionsLoading(false);
			});
	};

	const handleMarkAsPaid = () => {
		setMarkPaidLoading(true);
		requestMarkInvoiceAsPaid(id, { status: "PAID" })
			.then(() => {
				notification.success({
					message: "Success",
					description: "Invoice marked as paid successfully",
				});
				setInvoice((prev) => ({
					...prev,
					status: "PAID",
				}));
			})
			.catch((error) => {
				console.error("Error marking invoice as paid:", error);
				notification.error({
					message: "Error",
					description: error.response?.data?.message || "Failed to mark invoice as paid",
				});
			})
			.finally(() => {
				setMarkPaidLoading(false);
			});
	};

	const handleCreateTransaction = () => {
		setTransactionModalVisible(true);
		transactionForm.resetFields();
	};

	const handleTransactionSubmit = (values) => {
		setTransactionLoading(true);
		const payload = {
			invoiceId: id,
			method: values.method,
			channel: values.channel,
		};

		requestCreateTransaction(payload)
			.then(() => {
				notification.success({
					message: "Success",
					description: "Transaction created successfully",
				});
				setTransactionModalVisible(false);
				transactionForm.resetFields();
				// Refresh invoice details and transactions to show new transaction
				fetchInvoiceDetails();
				fetchTransactions();
			})
			.catch((error) => {
				console.error("Error creating transaction:", error);
				notification.error({
					message: "Error",
					description: error.response?.data?.message || "Failed to create transaction",
				});
			})
			.finally(() => {
				setTransactionLoading(false);
			});
	};

	const handleTransactionCancel = () => {
		setTransactionModalVisible(false);
		transactionForm.resetFields();
	};

	const handleProvisionInstances = () => {
		setProvisionLoading(true);
		const payload = {
			invoiceId: id,
		};

		requestProvisionInstancesFromInvoice(payload)
			.then(() => {
				notification.success({
					message: "Success",
					description: "Instances provisioned successfully",
				});
				// Refresh invoice details to show updated instance information
				fetchInvoiceDetails();
			})
			.catch((error) => {
				console.error("Error provisioning instances:", error);
				notification.error({
					message: "Error",
					description: error.response?.data?.message || "Failed to provision instances",
				});
			})
			.finally(() => {
				setProvisionLoading(false);
			});
	};

	const handleRegisterDomains = () => {
		setRegisterLoading(true);
		const payload = {
			invoiceId: id,
		};

		requestRegisterDomainsFromInvoice(payload)
			.then(() => {
				notification.success({
					message: "Success",
					description: "Domains registered successfully",
				});
				// Refresh invoice details to show updated domain information
				fetchInvoiceDetails();
			})
			.catch((error) => {
				console.error("Error registering domains:", error);
				notification.error({
					message: "Error",
					description: error.response?.data?.message || "Failed to register domains",
				});
			})
			.finally(() => {
				setRegisterLoading(false);
			});
	};

	const handleShareInvoice = (invoiceId) => {
		if (link) {
			navigator.clipboard.writeText(link).then(() => {
				notification.success({
					message: "Link Copied",
					description: "The invoice link has been copied to your clipboard.",
				});
			});
		} else {
			requestShareInvoiceToPublic(invoiceId)
				.then((response) => {
					setLink(response.data.link);
					navigator.clipboard.writeText(response.data.link);
					notification.success({
						message: "Link Shared",
						description: "The invoice link has been shared successfully.",
					});
				})
				.catch((error) => {
					notification.error({
						message: "Error",
						description: error.response?.data?.message || "Failed to share invoice link",
					});
				});
		}
	};

	const getStatusColor = (status) => {
		switch (status) {
			case "DRAFT":
				return "default";
			case "PENDING":
				return "orange";
			case "PAID":
				return "green";
			case "OVERDUE":
				return "red";
			case "CANCELLED":
				return "red";
			default:
				return "default";
		}
	};

	const getStatusText = (status) => {
		switch (status) {
			case "DRAFT":
				return "Draft";
			case "PENDING":
				return "Pending";
			case "PAID":
				return "Paid";
			case "OVERDUE":
				return "Overdue";
			case "CANCELLED":
				return "Cancelled";
			default:
				return status;
		}
	};

	const getTransactionStatusColor = (status) => {
		switch (status) {
			case "PENDING":
				return "orange";
			case "COMPLETED":
			case "SUCCESS":
				return "green";
			case "FAILED":
			case "CANCELLED":
				return "red";
			default:
				return "default";
		}
	};

	const getTransactionStatusText = (status) => {
		switch (status) {
			case "PENDING":
				return "Pending";
			case "COMPLETED":
			case "SUCCESS":
				return "Completed";
			case "FAILED":
				return "Failed";
			case "CANCELLED":
				return "Cancelled";
			default:
				return status;
		}
	};

	const itemColumns = [
		{
			title: "Type",
			key: "type",
			width: 100,
			render: (_, item) => (
				<Tag color={item.type === "DOMAIN" ? "green" : "blue"}>
					{item.type === "DOMAIN" ? "Domain" : "Instance"}
				</Tag>
			),
		},
		{
			title: "Item Details",
			key: "itemDetails",
			render: (_, item) => {
				if (item.type === "DOMAIN") {
					// Domain item
					return (
						<div style={{ display: "flex", alignItems: "center" }}>
							<PiPackageDuotone size={16} style={{ marginRight: 8, color: "#52c41a" }} />
							<div>
								<div style={{ fontWeight: 500 }}>Domain {item.domain?.name || "N/A"}</div>
								<div style={{ fontSize: "12px", color: "#666" }}>
									{item.domain?.extension?.name} ({item.domain?.extension?.code})
									{/* {item.domain?._id && ` • ID: ${item.domain._id}`} */}
								</div>
							</div>
						</div>
					);
				}
				// Instance item
				return (
					<div style={{ display: "flex", alignItems: "center" }}>
						<PiPackageDuotone size={16} style={{ marginRight: 8, color: "#1890ff" }} />
						<div>
							<div style={{ fontWeight: 500 }}>{item.instance?.service?.name || "N/A"}</div>
							<div style={{ fontSize: "12px", color: "#666" }}>
								{item.instance?.plan?.name} ({item.instance?.plan?.code})
								{item.instance?.region?.name && ` • ${item.instance.region.name}`}
								{/* {item.instance?._id && ` • Instance: ${item.instance._id}`} */}
							</div>
						</div>
					</div>
				);
			},
		},
		{
			title: "Subtotal",
			dataIndex: "unitSubtotal",
			key: "unitSubtotal",
			width: 120,
			align: "center",
			render: (value) => {
				return (
					<div style={{ textAlign: "center" }}>{formatCurrency(value, invoice?.currency)}</div>
				);
			},
		},
		{
			title: "Quantity",
			dataIndex: "quantity",
			key: "quantity",
			width: 120,
			align: "center",
			render: (quantity, item) => (
				<Space size={2}>
					<div style={{ fontWeight: 500 }}>{quantity}</div>
					{item.type === "DOMAIN" ? (
						<div style={{ fontSize: "12px", color: "#666" }}>year{quantity > 1 ? "s" : ""}</div>
					) : (
						item.instance?.plan?.price?.interval && (
							<div style={{ fontSize: "12px", color: "#666" }}>
								{item.instance.plan.price.interval?.toLowerCase()}
							</div>
						)
					)}
				</Space>
			),
		},
		{
			title: "Total",
			dataIndex: "lineTotal",
			key: "lineTotal",
			width: 120,
			align: "center",
			render: (value) => {
				return (
					<div style={{ textAlign: "center" }}>{formatCurrency(value, invoice?.currency)}</div>
				);
			},
		},
		{
			title: "Status",
			key: "status",
			width: 120,
			align: "center",
			render: (_, item) => {
				if (item.type === "DOMAIN") {
					// Domain items - show domain status or generic active status
					if (item.domain._id) {
						return (
							<Link to={`/domains/${item.domain._id}`}>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										marginBottom: 4,
									}}>
									<PiGlobeDuotone size={16} style={{ marginRight: 4, color: "#52c41a" }} />
									<Tag color="green" size="small">
										Registered
									</Tag>
								</div>
							</Link>
						);
					} else {
						return (
							<div style={{ textAlign: "center" }}>
								<Tag color="red" size="small">
									Inactive
								</Tag>
							</div>
						);
					}
				}
				// Instance items - show provisioning status
				return (
					<div style={{ textAlign: "center" }}>
						{item.instance?._id ? (
							<Link to={`/instances/${item.instance._id}`}>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										marginBottom: 4,
									}}>
									<PiCloudDuotone size={16} style={{ marginRight: 4, color: "#52c41a" }} />
									<Tag color="green" size="small">
										Provisioned
									</Tag>
								</div>
							</Link>
						) : (
							<div style={{ color: "#999" }}>
								<PiCloudDuotone size={16} style={{ marginBottom: 4, opacity: 0.5 }} />
								<div style={{ fontSize: "12px" }}>Not provisioned</div>
							</div>
						)}
					</div>
				);
			},
		},
	];

	if (loading) {
		return (
			<div className="page-content">
				<div style={{ textAlign: "center", padding: "50px 0" }}>
					<Spin size="large" />
				</div>
			</div>
		);
	}

	if (!invoice) {
		return (
			<div className="page-content">
				<div style={{ textAlign: "center", padding: "50px 0" }}>
					<Text>Invoice not found</Text>
				</div>
			</div>
		);
	}

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space split={<Divider type="vertical" />}>
					<Space>
						<Button icon={<PiArrowLeftDuotone />} onClick={() => navigate("/invoices")} />
						<Title level={4} style={{ margin: 0 }}>
							Invoice Details
						</Title>
						<Tag color={getStatusColor(invoice.status)}>{getStatusText(invoice.status)}</Tag>
					</Space>
				</Space>
			</div>

			<div className="page-content-wrapper">
				<Row gutter={24}>
					{/* Invoice Information */}
					<Col span={16}>
						<Card
							title={
								<Space>
									<PiReceiptDuotone size={20} />
									<span>Invoice Information</span>
								</Space>
							}
							extra={
								<Space>
									{invoice.status === "UNPAID" && (
										<>
											<Button
												type="default"
												icon={<PiCreditCardDuotone />}
												onClick={handleCreateTransaction}>
												Add Transaction
											</Button>
											<Popconfirm
												title="Mark as Paid"
												description="Are you sure you want to mark this invoice as paid?"
												onConfirm={handleMarkAsPaid}
												okText="Yes"
												cancelText="No">
												<Button
													type="primary"
													icon={<PiCheckCircleDuotone />}
													loading={markPaidLoading}>
													Mark as Paid
												</Button>
											</Popconfirm>
										</>
									)}
									{invoice.status === "PAID" &&
										invoice.items
											?.filter((it) => it.type === "INSTANCE")
											.some((item) => !item.instance._id) && (
											<Popconfirm
												title="Provision Instances"
												description="This will provision instances for all items in this invoice. Are you sure?"
												onConfirm={handleProvisionInstances}
												okText="Yes, Provision"
												cancelText="Cancel">
												<Button type="primary" icon={<PiCloudDuotone />} loading={provisionLoading}>
													Provision Instances
												</Button>
											</Popconfirm>
										)}
									{invoice.status === "PAID" &&
										invoice.items
											?.filter((it) => it.type === "DOMAIN")
											.some((item) => !item.domain._id) && (
											<Popconfirm
												title="Register Domains"
												description="This will register domains for all items in this invoice. Are you sure?"
												onConfirm={handleRegisterDomains}
												okText="Yes, Register"
												cancelText="Cancel">
												<Button type="primary" icon={<PiGlobeDuotone />} loading={registerLoading}>
													Register Domains
												</Button>
											</Popconfirm>
										)}
									{invoice.order && (
										<Button
											type="dashed"
											icon={<PiShoppingCartDuotone />}
											onClick={() => navigate(`/orders/${invoice.order._id}`)}>
											View Order
										</Button>
									)}
									<Button
										type="dashed"
										icon={<PiShareDuotone />}
										onClick={() => {
											if (link) {
												navigator.clipboard.writeText(link);
												message.success("Invoice link copied to clipboard");
											} else {
												handleShareInvoice(invoice._id);
											}
										}}></Button>
								</Space>
							}
							style={{ marginBottom: 24 }}>
							<Descriptions column={2} bordered>
								<Descriptions.Item label="Invoice Number" span={1}>
									<Text strong>{invoice.number}</Text>
								</Descriptions.Item>
								<Descriptions.Item label="Status" span={1}>
									<Tag color={getStatusColor(invoice.status)}>{getStatusText(invoice.status)}</Tag>
								</Descriptions.Item>
								<Descriptions.Item label="Currency" span={1}>
									<Tag color="blue">{invoice.currency}</Tag>
								</Descriptions.Item>
								<Descriptions.Item label="Total Items" span={1}>
									<Badge count={invoice.items?.length || 0} showZero />
								</Descriptions.Item>
								<Descriptions.Item label="Issued Date" span={1}>
									<Space>
										<PiCalendarDuotone />
										{dayjs(invoice.issuedAt).format("DD MMMM YYYY, HH:mm")}
									</Space>
								</Descriptions.Item>
								{invoice.expiredAt && (
									<Descriptions.Item label="Due Date" span={1}>
										<Space>
											<PiCalendarDuotone />
											{dayjs(invoice.expiredAt).format("DD MMMM YYYY, HH:mm")}
										</Space>
									</Descriptions.Item>
								)}
								{invoice.order && (
									<Descriptions.Item label="Related Order" span={2}>
										<Space>
											<PiShoppingCartDuotone />
											<Button
												type="link"
												style={{ padding: 0 }}
												onClick={() => navigate(`/orders/${invoice.order._id}`)}>
												{invoice.order.number}
											</Button>
										</Space>
									</Descriptions.Item>
								)}
							</Descriptions>
						</Card>

						{/* Invoice Items */}
						<Card
							title={
								<Space>
									<PiPackageDuotone size={20} />
									<span>Invoice Items</span>
									<Badge count={invoice.items?.length || 0} />
								</Space>
							}
							style={{ marginBottom: 24 }}>
							<Table
								columns={itemColumns}
								dataSource={invoice.items}
								pagination={false}
								rowKey={(item, index) => `${invoice._id}-${index}`}
								size="small"
								summary={(pageData) => {
									const subtotal = invoice.subtotal || 0;
									const tax = invoice.taxTotal || 0;
									const discount = invoice.discountTotal || 0;
									const total = invoice.total || 0;

									// show tax list, group by invoice.items.taxes.code
									const taxGroups = invoice.items.reduce((acc, item) => {
										item.taxes.forEach((tax) => {
											if (!acc[tax.code]) {
												acc[tax.code] = {
													name: tax.name,
													amount: 0,
												};
											}
											acc[tax.code].amount += tax.amount;
										});
										return acc;
									}, {});

									return (
										<Table.Summary>
											<Table.Summary.Row>
												<Table.Summary.Cell index={0} colSpan={5}>
													<Text strong>Subtotal</Text>
												</Table.Summary.Cell>
												<Table.Summary.Cell index={1} align="right">
													<Text strong>{formatCurrency(subtotal, invoice.currency)}</Text>
												</Table.Summary.Cell>
											</Table.Summary.Row>
											{discount > 0 && (
												<Table.Summary.Row>
													<Table.Summary.Cell index={0} colSpan={5}>
														<Text type="success">Discount</Text>
													</Table.Summary.Cell>
													<Table.Summary.Cell index={1} align="right">
														<Text type="success">
															-{formatCurrency(discount, invoice.currency)}
														</Text>
													</Table.Summary.Cell>
												</Table.Summary.Row>
											)}
											{Object.entries(taxGroups).length > 0 &&
												Object.entries(taxGroups).map(([code, { name, amount }]) => (
													<Table.Summary.Row key={code}>
														<Table.Summary.Cell index={0} colSpan={5}>
															<Text>{name}</Text>
														</Table.Summary.Cell>
														<Table.Summary.Cell index={1} align="right">
															<Text>{formatCurrency(amount, invoice.currency)}</Text>
														</Table.Summary.Cell>
													</Table.Summary.Row>
												))}
											{/* {tax > 0 && (
												<Table.Summary.Row>
													<Table.Summary.Cell index={0} colSpan={5}>
														<Text>Tax</Text>
													</Table.Summary.Cell>
													<Table.Summary.Cell index={1} align="right">
														<Text>{formatCurrency(tax, invoice.currency)}</Text>
													</Table.Summary.Cell>
												</Table.Summary.Row>
											)} */}
											<Table.Summary.Row>
												<Table.Summary.Cell index={0} colSpan={5}>
													<Text strong style={{ fontSize: "16px" }}>
														Grand Total
													</Text>
												</Table.Summary.Cell>
												<Table.Summary.Cell index={1} align="right">
													<Text strong style={{ fontSize: "16px", color: "#1890ff" }}>
														{formatCurrency(total, invoice.currency)}
													</Text>
												</Table.Summary.Cell>
											</Table.Summary.Row>
										</Table.Summary>
									);
								}}
							/>
						</Card>

						{/* Payment History */}
						<Card
							title={
								<Space>
									<PiCreditCardDuotone size={20} />
									<span>Payment History</span>
									<Badge count={transactions.length} />
								</Space>
							}
							style={{ marginBottom: 24 }}
							loading={transactionsLoading}>
							{transactions.length > 0 ? (
								<div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
									{transactions.map((transaction) => (
										<div
											key={transaction._id}
											style={{
												border: "1px solid #f0f0f0",
												borderRadius: "8px",
												padding: "16px",
												backgroundColor: "#fafafa",
											}}>
											<div
												style={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "flex-start",
													marginBottom: "8px",
												}}>
												<div>
													<div
														style={{
															fontWeight: 500,
															marginBottom: "4px",
															display: "flex",
															alignItems: "center",
															gap: "8px",
														}}>
														{transaction.number}
														<Tag color={getTransactionStatusColor(transaction.status)}>
															{getTransactionStatusText(transaction.status)}
														</Tag>
													</div>
													<div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>
														{transaction.team?.name || "Unknown Team"}
													</div>
													<div style={{ fontSize: "12px", color: "#666" }}>
														{dayjs(transaction.createdAt).format("DD MMM YYYY, HH:mm")}
													</div>
												</div>
												<div style={{ textAlign: "right" }}>
													<div style={{ fontWeight: 600, fontSize: "16px", marginBottom: "4px" }}>
														{formatCurrency(transaction.amount, transaction.currency)}
													</div>
													{transaction.metadata?.invoiceNumber && (
														<div style={{ fontSize: "12px", color: "#666" }}>
															Ref: {transaction.metadata.invoiceNumber}
														</div>
													)}
												</div>
											</div>
											<div
												style={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "center",
												}}>
												<div
													style={{ display: "flex", gap: "12px", fontSize: "12px", color: "#666" }}>
													<span>
														<strong>Method:</strong> {transaction.method.replace("_", " ")} (
														{transaction.channel.replace("_", " ")})
													</span>
													<span>
														<strong>Category:</strong> {transaction.category}
													</span>
												</div>
												<Button
													type="dashed"
													onClick={() => navigate(`/transactions/${transaction._id}`)}>
													View Details
												</Button>
											</div>
										</div>
									))}
								</div>
							) : (
								<div style={{ textAlign: "center", padding: "20px 0", color: "#666" }}>
									<PiCreditCardDuotone size={48} style={{ marginBottom: "8px", opacity: 0.5 }} />
									<div>No payment history available</div>
								</div>
							)}
						</Card>
					</Col>

					{/* Billing & Additional Info */}
					<Col span={8}>
						{/* Billing Address */}
						<Card
							title={
								<Space>
									<PiUserDuotone size={20} />
									<span>Billing Address</span>
								</Space>
							}
							style={{ marginBottom: 24 }}>
							{invoice.billingAddress ? (
								<div>
									<div style={{ marginBottom: 8 }}>
										<Text strong>{invoice.billingAddress.name}</Text>
									</div>
									<div style={{ marginBottom: 4 }}>
										<Text>{invoice.billingAddress.email}</Text>
									</div>
									<div style={{ marginBottom: 4 }}>
										<Text>{invoice.billingAddress.phone}</Text>
									</div>
									{invoice.billingAddress.address && (
										<>
											<Divider style={{ margin: "8px 0" }} />
											<div style={{ fontSize: "12px", color: "#666" }}>
												<div>{invoice.billingAddress.address}</div>
												<div>
													{invoice.billingAddress.city}, {invoice.billingAddress.state}{" "}
													{invoice.billingAddress.postalCode}
												</div>
												<div>{invoice.billingAddress.country}</div>
											</div>
										</>
									)}
								</div>
							) : (
								<Text type="secondary">No billing address available</Text>
							)}
						</Card>

						{/* Financial Summary */}
						<Card
							title={
								<Space>
									<PiCurrencyDollarDuotone size={20} />
									<span>Financial Summary</span>
								</Space>
							}
							style={{ marginBottom: 24 }}>
							<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
								<div style={{ display: "flex", justifyContent: "space-between" }}>
									<Text>Subtotal:</Text>
									<Text>{formatCurrency(invoice.subtotal || 0, invoice.currency)}</Text>
								</div>
								{invoice.discountTotal > 0 && (
									<div style={{ display: "flex", justifyContent: "space-between" }}>
										<Text type="success">Discount:</Text>
										<Text type="success">
											-{formatCurrency(invoice.discountTotal, invoice.currency)}
										</Text>
									</div>
								)}
								{invoice.taxTotal > 0 && (
									<div style={{ display: "flex", justifyContent: "space-between" }}>
										<Text>Tax:</Text>
										<Text>{formatCurrency(invoice.taxTotal, invoice.currency)}</Text>
									</div>
								)}
								<Divider style={{ margin: "8px 0" }} />
								<div style={{ display: "flex", justifyContent: "space-between" }}>
									<Text strong style={{ fontSize: "16px" }}>
										Grand Total:
									</Text>
									<Text strong style={{ fontSize: "16px", color: "#1890ff" }}>
										{formatCurrency(invoice.total || 0, invoice.currency)}
									</Text>
								</div>
							</div>
						</Card>

						{/* Payment Status */}
						<Card
							title={
								<Space>
									<PiCheckCircleDuotone size={20} />
									<span>Payment Status</span>
								</Space>
							}
							style={{ marginBottom: 24 }}>
							<div style={{ textAlign: "center", padding: "20px 0" }}>
								<Tag
									color={getStatusColor(invoice.status)}
									style={{ fontSize: "14px", padding: "8px 16px" }}>
									{getStatusText(invoice.status)}
								</Tag>
								{invoice.status === "PAID" && invoice.paidAt && (
									<div style={{ marginTop: 12, fontSize: "12px", color: "#666" }}>
										Paid on {dayjs(invoice.paidAt).format("DD MMMM YYYY, HH:mm")}
									</div>
								)}
								{invoice.status === "PENDING" && invoice.expiredAt && (
									<div style={{ marginTop: 12, fontSize: "12px", color: "#666" }}>
										Due {dayjs(invoice.expiredAt).format("DD MMMM YYYY")}
									</div>
								)}
							</div>
						</Card>

						{/* Metadata */}
						{invoice.metadata && Object.keys(invoice.metadata).length > 0 && (
							<Card
								title={
									<Space>
										<PiInfoDuotone size={20} />
										<span>Additional Information</span>
									</Space>
								}>
								<div style={{ backgroundColor: "#f5f5f5", padding: "12px", borderRadius: "4px" }}>
									<pre style={{ margin: 0, fontSize: "12px" }}>
										{JSON.stringify(invoice.metadata, null, 2)}
									</pre>
								</div>
							</Card>
						)}
					</Col>
				</Row>
			</div>

			{/* Transaction Modal */}
			<Modal
				title={
					<Space>
						<PiCreditCardDuotone />
						<span>Add Transaction</span>
					</Space>
				}
				open={transactionModalVisible}
				onCancel={handleTransactionCancel}
				footer={null}
				width={500}>
				<Form
					form={transactionForm}
					layout="vertical"
					onFinish={handleTransactionSubmit}
					style={{ marginTop: 16 }}>
					<Form.Item
						label="Method"
						name="method"
						rules={[{ required: true, message: "Please select a payment method" }]}>
						<Select placeholder="Select payment method">
							<Select.Option value="VIRTUAL_ACCOUNT">Virtual Account</Select.Option>
							<Select.Option value="CASH">Cash</Select.Option>
						</Select>
					</Form.Item>
					{/* channel */}
					<Form.Item
						label="Channel"
						name="channel"
						rules={[{ required: true, message: "Please select a payment channel" }]}>
						<Select placeholder="Select payment channel">
							{/* virtual account */}
							<Select.Option value="AGI_VA">AGI VA</Select.Option>
							<Select.Option value="ATM_VA">ATM VA</Select.Option>
							<Select.Option value="BNC_VA">BNC VA</Select.Option>
							<Select.Option value="BNI_VA">BNI VA</Select.Option>
							<Select.Option value="BRI_VA">BRI VA</Select.Option>
							<Select.Option value="BSI_VA">BSI VA</Select.Option>
							<Select.Option value="CIMB_VA">CIMB VA</Select.Option>
							<Select.Option value="DANAMON_VA">DANAMON VA</Select.Option>
							<Select.Option value="MANDIRI_VA">MANDIRI VA</Select.Option>
							<Select.Option value="MAYBANK_VA">MAYBANK VA</Select.Option>
							<Select.Option value="PERMATA_VA">PERMATA VA</Select.Option>
							<Select.Option value="SAMPOERNA_VA">SAMPOERNA VA</Select.Option>
							{/* cash */}
							<Select.Option value="CASH">Cash</Select.Option>
						</Select>
					</Form.Item>

					<Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
						<Space style={{ width: "100%", justifyContent: "flex-end" }}>
							<Button onClick={handleTransactionCancel}>Cancel</Button>
							<Button type="primary" htmlType="submit" loading={transactionLoading}>
								Create Transaction
							</Button>
						</Space>
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
}

export default InvoiceViewPage;
